import {Composition} from "../../engine/composition";
import {CompositionEventsEnum} from "../../librettoEditor/CompositionEvents";
import {SEQUENCE_CANVAS_ID, SEQUENCE_COMPOSITION_CONTAINER_ID} from "../config/config";
import {Timestamp} from "../../engine/models";
import {VideoClip} from "../../engine/clips/video/video";
import {AudioClip, Clip, MediaClip} from "../../engine/clips";
import {ImageClip} from "../../engine/clips/image/image";
import {SequenceStateStore} from "../store/SequenceStateStore";
import { SequencePersistenceService } from "./SequencePersistenceService";
import { ImageSourceRef } from "../../engine/sources/image-source";
import {ALL_FORMATS, Input, UrlSource} from "mediabunny";
import {MediaSourceRef} from "../../engine/sources/media-source";
import {Track} from "../../engine/tracks";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
import SequenceCompositionBridge from "../SequenceCompositionBridge";
import {CreateClipForComposition} from "../../pages/V2Editor/utils";
import {SequenceApplicationStore} from "../store/SequenceApplicationStore";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import {CompositionService} from "../../librettoEditor/services/CompositionService";

/**
 * Local (non-singleton) composition manager for the Sequence Editor modal.
 * Owns its Composition instance and attaches to a provided canvas element.
 */
export class SequenceCompositionService {
    private static _composition: Composition | null = null;

    private static _compositionMounted = false;

    public static get composition(): Composition {
        if (!this._composition) {
            throw new Error("Sequence composition not initialized. Call initialize() first.");
        }
        return this._composition;
    }

    static isInitialized(): boolean {
        return this._composition !== null;
    }

    /**
     * Create a new Composition instance (if not already created).
     * Does not attach to DOM; call attachToCanvas next.
     */
    static async initialize(): Promise<void> {
        this._compositionMounted = false;
        if (this._composition) {
            console.warn("Composition already initialized");
            return;
        }

        // Create new composition instance
        this._composition = new Composition({
            width: 1920,
            height: 1080,
            background: '#000000',
            backend: 'webgl',
        });

        // Canvas mounting is handled by SequenceCanvasSection once DOM elements are available.
        console.log("Composition service initialized");
    }

    static mountComposition(): boolean {
        if (!this._composition) {
            return false;
        }
        if (this._compositionMounted) {
            return true;
        }
        const container = document.getElementById(SEQUENCE_COMPOSITION_CONTAINER_ID) as HTMLDivElement;
        const canvas = document.getElementById(SEQUENCE_CANVAS_ID) as HTMLCanvasElement;

        if (!container || !canvas) {
            return false;
        }

        // Mount the composition to the player element
        this.composition.attachCanvasToComposition(canvas);

        // Calculate and apply scaling
        const scale = Math.min(
            container.clientWidth / this.composition.settings.width,
            container.clientHeight / this.composition.settings.height,
        );

        // Set the player dimensions and scaling
        canvas.style.width = `${this.composition.settings.width}px`;
        canvas.style.height = `${this.composition.settings.height}px`;
        canvas.style.transform = `scale(${scale})`;
        canvas.style.transformOrigin = 'center';
        this._compositionMounted = true;
        return true;
    }

    static async play() {
        // Guard against dual playback when sequence modal is open over the main editor.
        try {
            if (CompositionService.isInitialized()) {
                await CompositionService.pause();
            }
        } catch {}
        await this.composition.play();
    }

    static async pause() {
        await this.composition.pause();
    }

    static async seek(seconds: number) {
        const frames = Math.round(seconds * this.composition.fps);
        await this.composition.seek(frames);
    }

    static cleanup() {
        // Allow GC to reclaim the composition; caller should ensure audio is paused
        this._composition = null;
    }

    /**
     * Restore a sequence from persistence.
     * Caller supplies resolveAssetUrl(assetId, type) -> url to fetch media.
     * This method sets the restoration flag to prevent persistence updates during restore.
     */
    static async restoreFromPersistence(sequenceId: string, resolveAssetUrl: (assetId: string, type: 'audio'|'video'|'image') => Promise<string | undefined>, headers?: Record<string, string>): Promise<void> {
        if (!this._composition) {
            console.warn("Cannot restore: composition not initialized");
            return;
        }

        // Import bridge to set restoration flag
        const bridge = SequenceCompositionBridge;
        bridge.setRestoring(true);

        try {
            const { layers: layersWithClips } = await SequencePersistenceService.fetchClipsByLayerOrdered(sequenceId);
            
            // Restore layers in order (bottom to top for correct stacking)
            for (const layerData of layersWithClips) {
                const { layerId, name: layerName, clips: clipRecords } = layerData;
                let track: Track<any> | null = null;

                for (const rec of clipRecords) {
                    try {
                        const type = String(rec.type).toLowerCase();
                        const assetId = rec.assetId;
                        const clip = await CreateClipForComposition({type: type, id: rec.id, assetId: assetId, assetTitle: rec.name, assetResolution: "", composition: SequenceCompositionService.composition, stateStore: SequenceStateStore, resolveAssetUrl});

                        if ('range' in clip) {
                            const inSec = Number(rec.inSec ?? 0);
                            const outSec = Number(rec.outSec ?? 0);
                            if (Number.isFinite(inSec) && Number.isFinite(outSec) && outSec > inSec) {
                                clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
                            }
                            // Always restore offset (even when range is missing/invalid).
                            const off = Number(rec.offsetSec ?? 0);
                            clip.offset = Timestamp.fromSeconds(Number.isFinite(off) ? off : 0);
                        }
                        if (typeof rec.startSec === 'number') {
                            if ('range' in clip && 'offset' in clip) {
                                const inSec = clip.range?.[0]?.seconds ?? 0;
                                const newOffset = Math.max(0, rec.startSec - inSec);
                                clip.offset = Timestamp.fromSeconds(newOffset);
                            } else if ('start' in clip) {
                                clip.start = Timestamp.fromSeconds(rec.startSec);
                            }
                        }
                        clip.gain = Number(rec?.props?.gain ?? 1);
                        clip.playbackRate = Number(rec?.props?.playbackRate ?? 1);
                        if ('alpha' in clip) clip.alpha = Number(rec?.props?.alpha ?? 1);

                        // Restore waveformColor if present
                        if (rec.waveformColor) {
                            SequenceStateStore.setWaveformColor(clip.id, rec.waveformColor);
                        }

                        // Insert into composition (creates a track automatically)
                        if (!track) {
                            track = await this.composition.add(clip, layerId);
                            // Restore layer name after first clip is added (when track/layer is created)
                            // The LAYER_ADDED event should have fired and added the layer to the store
                            if (layerName && SequenceStateStore.layers[layerId]) {
                                SequenceStateStore.setLayerName(layerId, layerName);
                            }
                        } else {
                            await track.add(clip);
                        }
                    } catch (e) {
                        console.error('[SequenceCompositionService] Failed to restore clip', e);
                    }
                }
            }
        } catch (e) {
            console.error('[SequenceCompositionService] Failed to restore from persistence', e);
        } finally {
            bridge.setRestoring(false);
        }
    }

    static createLayer(index?: number) {
        // TODO: Implement when needed
    }

    static async moveClipToLayer(clipId: string, layerId: string) {
        // TODO: Implement when needed
    }

    static getClipById(clipId: string) {
        const comp = this._composition;
        if (!comp) return undefined;
        for (const tr of comp.tracks) {
            // @ts-ignore
            const found = tr.clips.find((c: any) => c.id === clipId);
            if (found) return found;
        }
        return undefined;
    }

    static async splitClip() {
        const clipId = SequenceApplicationStore.selectedClipId;
        if (!clipId) return;

        const splitTime = SequenceStateStore.currentTime;
        if (splitTime === undefined) return;

        const clip = this.getClipById(clipId);
        if (!clip || !clip.track) return;

        // Use clone() to create the right clip - this copies offset, range, etc.
        let rightClip: VideoClip | AudioClip | ImageClip | null = null;
        if (clip.type === 'video' && clip instanceof VideoClip) {
            rightClip = clip.clone();
        } else if (clip.type === 'audio' && clip instanceof AudioClip) {
            rightClip = clip.clone();
        } else if (clip.type === 'image' && clip instanceof ImageClip) {
            rightClip = clip.copy();
        }
        if (!rightClip) return;

        // Copy state store info
        SequenceStateStore.setAssetIdForClip(rightClip.id, SequenceStateStore.clipIdToAssetId[clip.id]);
        SequenceStateStore.setTitleForClip(rightClip.id, SequenceStateStore.clipIdToTitle[clip.id]);
        SequenceStateStore.setWaveformColor(rightClip.id, SequenceStateStore.clipIdToWaveformColor[clip.id]);
        if (clip.type === "image") {
            SequenceStateStore.setThumbnailIdForClip(rightClip.id, SequenceStateStore.clipIdToThumbnailId[clip.id]);
        }

        // Convert split time to source time
        const splitTimeInSource = clip.CompositionTimeToClipTime(splitTime);

        // Divide cuts between left and right clips
        const leftCuts: Array<{ id: string; startSec: number; endSec: number; groupId?: string }> = [];
        const rightCuts: Array<{ id: string; startSec: number; endSec: number; groupId?: string }> = [];

        if (clip instanceof MediaClip && Array.isArray(clip.cuts)) {
            for (const c of clip.cuts) {
                if (c.endSec <= splitTimeInSource) {
                    leftCuts.push(c);
                } else if (c.startSec >= splitTimeInSource) {
                    rightCuts.push({ ...c, id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}` });
                } else {
                    // Cut spans the split time, need to split it
                    leftCuts.push({ id: c.id, startSec: c.startSec, endSec: splitTimeInSource, groupId: c.groupId });
                    rightCuts.push({ id: crypto?.randomUUID?.() ?? `cut_${Date.now()}_${Math.random()}`, startSec: splitTimeInSource, endSec: c.endSec, groupId: c.groupId });
                }
            }
            clip.cuts = leftCuts;
            rightClip.cuts = rightCuts;
        }

        // Adjust ranges:
        // Left clip: keep original in, set out to split point
        // Right clip: set in to split point, keep original out
        const originalOutSec = (clip as MediaClip).range[1].seconds;
        
        // Left clip ends at the split point
        clip.stop = Timestamp.fromSeconds(splitTimeInSource);
        
        // Right clip starts at split point, ends at original out
        rightClip.range = [
            Timestamp.fromSeconds(splitTimeInSource),
            Timestamp.fromSeconds(originalOutSec)
        ];

        // Normalize and derive segments
        (clip as MediaClip).normalizeCuts();
        (clip as MediaClip).deriveSegments();
        rightClip.normalizeCuts();
        rightClip.deriveSegments();

        // Insert right clip after left
        const index = clip.track.clips.findIndex((c: any) => c.id === clip.id);
        await clip.track.add(rightClip, index + 1);
        this.composition?.onClipTimingChanged?.();

        (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, { clip });
        // For the new clip, the adding to the track triggers a clip added event.

        SequenceApplicationStore.setSelectedClipId(rightClip.id);
    }

    static getLayerById(layerId: string) {
        const comp = this._composition;
        if (!comp) return undefined;
        for (const tr of comp.tracks) {
            // @ts-ignore
            if (tr.id === layerId) return tr;
        }
        return undefined;
    }

    static async addClip(clip: Clip, assetId: string) {
        SequenceStateStore.setAssetIdForClip(clip.id, assetId);
        await this.composition.add(clip);
    }

    static deleteLayer(layerId: string) {
        const track: any = this.getLayerById(layerId);
        if (!track) {
            return;
        }
        try {
            // Prefer removing via the composition to ensure state/view is cleaned up
            this.composition.removeTrack(track);
        } catch (e) {
            console.error('deleteLayer failed:', e);
        }
    }

    static deleteClipById(clipId: string) {
        const clip: any = this.getClipById(clipId);
        if (!clip) {
            return;
        }
        try {
            // Prefer removing via the owning track to ensure state/view is cleaned up
            clip.track?.remove(clip);
        } catch (e) {
            console.error('deleteClipById failed:', e);
        }
    }

    static async duplicateClipById(clipId: string) {
        // TODO: Implement when needed
    }

    static moveClipTo(clipId: string, newStartSec: number) {
        const clip: any = this.getClipById(clipId);
        if (!clip) {
            return newStartSec;
        }
        const index = clip.track.clips.findIndex((c: any) => c.id === clip.id);
        let previousClipEndTime = 0;
        let nextClipStartTime = Infinity;
        if (index > 0) {
            const previousClip = clip.track.clips[index - 1];
            previousClipEndTime = previousClip.stop?.seconds ?? 0;
        }
        if (index < clip.track.clips.length - 1) {
            const nextClip = clip.track.clips[index + 1];
            nextClipStartTime = nextClip.start?.seconds ?? Infinity;
        }
        let clampedStart = newStartSec;
        try {
            clampedStart = Math.max(clampedStart, previousClipEndTime);
            clampedStart = Math.min(clampedStart, nextClipStartTime - (clip.timelineDuration?.seconds ?? 0) - 0.001);
            //Media clip path: start = range[0] + offset → offset = newStart - range[0]
            if ('range' in clip && 'offset' in clip) {
                const inSec = clip.range?.[0]?.seconds ?? 0;
                const newOffsetSec = clampedStart - inSec;
                clip.offset = Timestamp.fromSeconds(newOffsetSec);
            } else if ('start' in clip && 'offsetBy' in clip) {
                // Base clip path: move by delta
                const currentStartSec = clip.start?.seconds ?? 0;
                const deltaSec = clampedStart - currentStartSec;
                clip.offsetBy(Timestamp.fromSeconds(deltaSec));
            }
            (this.composition as any)?.trigger?.(CompositionEventsEnum.CLIP_UPDATED, {clip});
        } catch (e) {
            console.log('setClipStart failed:', e);
        }
        (this.composition as any)?.onClipTimingChanged?.();
        return clampedStart;
    }

    static getFitDuration(): number {
        if (!this._composition) return 0;
        return this.composition.duration.seconds;
    }
}
